import React from 'react';
import { FaBox, FaUsers, FaShoppingCart, FaSpinner } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { OrderLog } from '../../services/orderService';
import { formatCurrency } from '../../utils/commonUtils';

// =============================================================================
// Types
// =============================================================================

interface OrderStats {
  totalOrders: number;
  todayOrders: number;
  pendingOrders: number;
  preparingOrders: number;
  totalRevenue: number;
}

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ComponentType<any>;
  iconColor: string;
  iconBgColor: string;
  subtitleColor?: string;
  delay?: number;
}

// =============================================================================
// Individual Stat Card Component
// =============================================================================

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor,
  iconBgColor,
  subtitleColor = 'text-gray-600',
  delay = 0,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {typeof value === 'number' && title.includes('Gelir') ? formatCurrency(value) : value}
          </p>
        </div>
        <div className={`p-3 ${iconBgColor} rounded-xl`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
      {subtitle && (
        <div className="mt-4">
          <span className={`text-sm ${subtitleColor}`}>{subtitle}</span>
        </div>
      )}
    </motion.div>
  );
};

// =============================================================================
// Statistics Cards Grid Component
// =============================================================================

interface StatisticsCardsProps {
  stats: OrderStats | null;
}

export const StatisticsCards: React.FC<StatisticsCardsProps> = ({ stats }) => {
  const cards = [
    {
      title: 'Toplam Sipariş',
      value: stats?.totalOrders || 0,
      subtitle: `Bugün: ${stats?.todayOrders || 0} sipariş`,
      icon: FaShoppingCart,
      iconColor: 'text-blue-500',
      iconBgColor: 'bg-blue-50',
      delay: 0,
    },
    {
      title: 'Bekleyen',
      value: stats?.pendingOrders || 0,
      subtitle: 'İşlem bekliyor',
      icon: FaSpinner,
      iconColor: 'text-yellow-500',
      iconBgColor: 'bg-yellow-50',
      subtitleColor: 'text-yellow-600',
      delay: 0.1,
    },
    {
      title: 'Hazırlanıyor',
      value: stats?.preparingOrders || 0,
      subtitle: 'Aktif işlemler',
      icon: FaBox,
      iconColor: 'text-blue-500',
      iconBgColor: 'bg-blue-50',
      subtitleColor: 'text-blue-600',
      delay: 0.2,
    },
    {
      title: 'Toplam Gelir',
      value: stats?.totalRevenue || 0,
      subtitle: 'Tamamlanan siparişler',
      icon: FaUsers,
      iconColor: 'text-green-500',
      iconBgColor: 'bg-green-50',
      subtitleColor: 'text-green-600',
      delay: 0.3,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {cards.map((card, index) => (
        <StatCard key={index} {...card} />
      ))}
    </div>
  );
};

// =============================================================================
// Recent Order Item Component
// =============================================================================

interface RecentOrderItemProps {
  order: OrderLog;
  onOrderClick: (orderId: number) => void;
  getStatusText: (status: string) => string;
  getStatusColor: (status: string) => string;
  index: number;
}

const RecentOrderItem: React.FC<RecentOrderItemProps> = ({
  order,
  onOrderClick,
  getStatusText,
  getStatusColor,
  index,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer group"
      onClick={() => onOrderClick(order.id)}
    >
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
            {order.user_name} {order.user_surname}
          </p>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}
          >
            {getStatusText(order.status)}
          </span>
        </div>
        <p className="text-sm text-gray-600 line-clamp-1">
          {order.items_summary || 'Ürün bilgisi yok'}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {order.management} - {order.office}
        </p>
      </div>
      <div className="text-right ml-4">
        <p className="text-sm font-medium text-green-600">{formatCurrency(order.total_amount)}</p>
        <p className="text-xs text-gray-500">
          {new Date(order.created_at).toLocaleString('tr-TR')}
        </p>
      </div>
    </motion.div>
  );
};

// =============================================================================
// Recent Orders List Component
// =============================================================================

interface RecentOrdersListProps {
  orders: OrderLog[];
  onOrderClick: (orderId: number) => void;
  onViewAll: () => void;
  getStatusText: (status: string) => string;
  getStatusColor: (status: string) => string;
}

export const RecentOrdersList: React.FC<RecentOrdersListProps> = ({
  orders,
  onOrderClick,
  onViewAll,
  getStatusText,
  getStatusColor,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.4 }}
      className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Son Siparişler</h2>
        <button
          onClick={onViewAll}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors hover:underline"
        >
          Tümünü Gör
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FaShoppingCart className="mx-auto text-3xl mb-3 text-gray-300" />
          <p>Henüz sipariş bulunmuyor</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order, index) => (
            <RecentOrderItem
              key={order.id}
              order={order}
              onOrderClick={onOrderClick}
              getStatusText={getStatusText}
              getStatusColor={getStatusColor}
              index={index}
            />
          ))}
        </div>
      )}
    </motion.div>
  );
};

// =============================================================================
// Dashboard Header Component
// =============================================================================

interface DashboardHeaderProps {
  totalOrders: number;
  lastUpdateTime?: string;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  totalOrders,
  lastUpdateTime,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="mb-8"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h1>
          <p className="text-gray-600">Sipariş yönetim sistemi genel görünümü</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Toplam Sipariş</p>
          <p className="text-3xl font-bold text-blue-600">{totalOrders}</p>
          {lastUpdateTime && (
            <p className="text-xs text-gray-400 mt-1">Son güncelleme: {lastUpdateTime}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};
